
import React, { useState, useEffect } from 'react';
import { Loader2, Sparkles, ShieldAlert } from 'lucide-react';

interface AIGeneratedImageProps {
  prompt: string;
  alt: string;
  className?: string;
  aspectRatio?: "1:1" | "3:4" | "4:3" | "9:16" | "16:9";
}

// Pre-defined high-quality static assets to replace dynamic generation
// ensuring zero API usage and instant loading for all users.
// These images have been selected to match the visual intent of the original prompts.
const STATIC_ASSETS: Record<string, string> = {
  // About Section
  "Astra Neural Shield": "https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=2072&auto=format&fit=crop",
  
  // Services Section
  "Cybersecurity Domain": "https://images.unsplash.com/photo-1563986768609-322da13575f3?q=80&w=1600&auto=format&fit=crop",
  "AI Domain": "https://images.unsplash.com/photo-1677442136019-21780ecad995?q=80&w=2070&auto=format&fit=crop",
  "Digital Domain": "https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=2015&auto=format&fit=crop",

  // Industries Section
  "Finance & Banking": "https://images.unsplash.com/photo-1639322537228-f710d846310a?q=80&w=2532&auto=format&fit=crop",
  "Government & Defense": "https://images.unsplash.com/photo-1535868463750-c78d9543614f?q=80&w=2670&auto=format&fit=crop",
  "Healthcare Systems": "https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?q=80&w=2070&auto=format&fit=crop",
  "Quantum Commerce": "https://images.unsplash.com/photo-1512446816042-444d641267d4?q=80&w=2670&auto=format&fit=crop",
};

const AIGeneratedImage: React.FC<AIGeneratedImageProps> = ({ 
  prompt, 
  alt, 
  className = "", 
  aspectRatio = "1:1" 
}) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate a brief "loading" phase for the premium feel (Protocol Initialization), 
    // but use static assets to ensure zero API usage and perfect reliability.
    const loadTime = Math.random() * 500 + 300; // 0.3s - 0.8s random delay for effect
    
    const timer = setTimeout(() => {
      // Fallback to "Cybersecurity Domain" if a specific key isn't found
      const asset = STATIC_ASSETS[alt] || STATIC_ASSETS["Cybersecurity Domain"];
      setImageUrl(asset);
      setLoading(false);
    }, loadTime);

    return () => clearTimeout(timer);
  }, [alt]);

  if (loading) {
    return (
      <div className={`flex flex-col items-center justify-center bg-brand-950/40 backdrop-blur-xl border border-white/5 relative overflow-hidden ${className}`}>
        <div className="hologram-line" />
        <Loader2 className="w-10 h-10 text-brand-500 animate-spin mb-4" />
        <div className="flex items-center gap-2 text-[10px] font-black text-brand-400 uppercase tracking-[0.3em] animate-pulse">
          <Sparkles className="w-3 h-3" /> Initializing_Visual...
        </div>
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden group ${className}`}>
      <img 
        src={imageUrl!} 
        alt={alt} 
        className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent pointer-events-none" />
      <div className="hologram-line opacity-0 group-hover:opacity-30 transition-opacity" />
    </div>
  );
};

export default AIGeneratedImage;
